import { useMemo } from 'react'

import { useAuth } from '../auth/AuthProvider'
import type { OrganizationDto } from '../api/types'
import { isDemoUiEnabled } from '../dev/demoMode'
import { isPivotModeEnabled } from '../pivot/pivotMode'

export type BuyerEntitlements = {
  organization: OrganizationDto | null
  plan: string
  planDisplayName: string
  marketCatalogEnabled: boolean
  platformRfqEnabled: boolean
  platformChatEnabled: boolean
  buyerSuppliersEnabled: boolean
  marketPriceIntelligenceEnabled: boolean
  maxPrivateSuppliers: number | null
  privateSuppliersUsed: number | null
  privateSupplierRefreshDays: number | null
  isStartPlan: boolean
}

const defaultEntitlements = (): BuyerEntitlements => ({
  organization: null,
  plan: 'start',
  planDisplayName: 'Старт',
  marketCatalogEnabled: true,
  platformRfqEnabled: true,
  platformChatEnabled: true,
  buyerSuppliersEnabled: true,
  marketPriceIntelligenceEnabled: true,
  maxPrivateSuppliers: null,
  privateSuppliersUsed: null,
  privateSupplierRefreshDays: null,
  isStartPlan: false,
})

export const useBuyerEntitlements = (): BuyerEntitlements => {
  const { session, organizations, role } = useAuth()

  return useMemo(() => {
    if (role !== 'buyer' || !session?.organizationId) {
      return defaultEntitlements()
    }
    const organization = organizations.find((item) => item.id === session.organizationId) ?? null
    if (!organization) {
      return defaultEntitlements()
    }
    const plan = organization.plan ?? 'start'
    const pivotPremium = isPivotModeEnabled()
    return {
      organization,
      plan: pivotPremium ? 'premium' : plan,
      planDisplayName: pivotPremium
        ? 'Премиум'
        : (organization.plan_display_name ?? 'Старт'),
      marketCatalogEnabled: organization.market_catalog_enabled ?? true,
      platformRfqEnabled: organization.platform_rfq_enabled ?? true,
      platformChatEnabled: organization.platform_chat_enabled ?? true,
      buyerSuppliersEnabled: organization.buyer_suppliers_enabled ?? true,
      marketPriceIntelligenceEnabled:
        isDemoUiEnabled ||
        pivotPremium ||
        (organization.market_price_intelligence_enabled ?? false),
      maxPrivateSuppliers: pivotPremium ? null : (organization.max_private_suppliers ?? null),
      privateSuppliersUsed: organization.private_suppliers_used ?? null,
      privateSupplierRefreshDays: pivotPremium
        ? null
        : (organization.private_supplier_refresh_days ?? null),
      isStartPlan: pivotPremium ? false : plan === 'start',
    }
  }, [organizations, role, session?.organizationId])
}
